import { Badge } from 'src/design/atoms/Badge';
import { Text } from 'src/design/atoms/Text';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { ConfirmPopover } from 'src/design/molecules/ConfirmPopover';
import { IconPlayerPlay } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { RecurringBackgroundJobDto } from 'src/api/FiretrolApiClient';
import { useRecurringBackgroundJobs } from 'src/api/queries/admin/useBackgroundJobs';
import { useTriggerRecurringBackgroundJob } from 'src/api/queries/admin/useBackgroundJobMutations';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { formatDateTime } from 'src/utils';
import { jobStateColor } from 'src/routes/Settings/BackgroundJobs/jobActions';

const COLUMNS: MRT_ColumnDef<RecurringBackgroundJobDto>[] = [
  { accessorKey: 'id', header: 'ID', size: 200 },
  { accessorKey: 'jobName', header: 'Job', size: 280 },
  {
    accessorKey: 'cron',
    header: 'Cron',
    size: 130,
    Cell: ({ cell }) => (
      <Text size="xs" ff="monospace">
        {cell.getValue<string | undefined>() ?? '—'}
      </Text>
    ),
  },
  {
    accessorKey: 'queue',
    header: 'Queue',
    size: 110,
    Cell: ({ cell }) => cell.getValue<string | undefined>() ?? '—',
  },
  {
    accessorKey: 'timeZoneId',
    header: 'Time Zone',
    size: 150,
    Cell: ({ cell }) => cell.getValue<string | undefined>() ?? '—',
  },
  {
    accessorKey: 'lastExecution',
    header: 'Last Run',
    size: 170,
    Cell: ({ cell }) =>
      formatDateTime(cell.getValue<Date | undefined>()) || '—',
  },
  {
    accessorKey: 'lastJobState',
    header: 'Last State',
    size: 120,
    Cell: ({ cell }) => {
      const value = cell.getValue<string | undefined>();
      return value ? (
        <Badge color={jobStateColor(value)} size="sm">
          {value}
        </Badge>
      ) : (
        '—'
      );
    },
  },
  {
    accessorKey: 'nextExecution',
    header: 'Next Run',
    size: 170,
    Cell: ({ cell }) =>
      formatDateTime(cell.getValue<Date | undefined>()) || '—',
  },
  {
    accessorKey: 'error',
    header: 'Error',
    size: 220,
    Cell: ({ cell }) => {
      const value = cell.getValue<string | undefined>();
      return value ? (
        <Text size="xs" c="red" lineClamp={2}>
          {value}
        </Text>
      ) : (
        '—'
      );
    },
  },
];

export const RecurringBackgroundJobsTable: React.FC = () => {
  const recurringQuery = useRecurringBackgroundJobs();
  const triggerJob = useTriggerRecurringBackgroundJob();
  const recurring = useMemo(
    () => recurringQuery.data ?? [],
    [recurringQuery.data],
  );

  const tableState = useTableState({
    identifier: 'background-jobs-recurring',
    columns: COLUMNS,
    defaultSorting: [{ id: 'id', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    onRefresh: recurringQuery.refetch,
    tableOptions: {
      enableColumnFilters: false,
      enableRowActions: true,
      enableClickToCopy: false,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      columns: COLUMNS,
      data: recurring,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderRowActions: ({ row }) => {
        const job = row.original;
        if (!job.id) return null;
        return (
          <ConfirmPopover
            contentTitle="Trigger now?"
            contentBody={`Run "${job.jobName}" immediately?`}
            confirmButtonText="Trigger"
            confirmButtonIntent="teal"
            loading={triggerJob.isPending}
            // The popover awaits this, and a rejected mutateAsync would leave
            // the rejection unhandled; the toast is already raised by meta.
            onConfirmClick={() =>
              triggerJob.mutateAsync(job.id).catch(() => undefined)
            }
          >
            <BasicActionIcon variant="light" color="teal" label="Trigger">
              <IconPlayerPlay size={18} />
            </BasicActionIcon>
          </ConfirmPopover>
        );
      },
    },
    getRowId: (row) => row.id,
    totalCount: recurring.length,
    isLoading: recurringQuery.isLoading,
    isFetching: recurringQuery.isFetching,
    isError: recurringQuery.isError,
  });

  return table;
};
